import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import type { SlotId } from "../utils/hangingProtocolService";

/**
 * UI atoms for CaseViewer and shared layout state
 *
 * These atoms manage UI state that was previously passed as props
 * or stored in component useState. Benefits:
 * - State persists across component remounts
 * - No prop drilling for layout state
 * - Easier to implement keyboard shortcuts globally
 */

// =============================================================================
// Layout State
// =============================================================================

// Left sidebar collapsed state (persisted to localStorage)
export const leftSidebarCollapsedAtom = atomWithStorage("leftSidebarCollapsed", false);

// Right sidebar collapsed state (persisted to localStorage)
export const rightSidebarCollapsedAtom = atomWithStorage("rightSidebarCollapsed", false);

// Right sidebar width in px (persisted to localStorage). Default matches the
// previous fixed `w-[28rem]` (448px). Bounds enforced at the resize handler.
export const rightSidebarWidthAtom = atomWithStorage("rightSidebarWidth", 448);

export const RIGHT_SIDEBAR_MIN_WIDTH = 320;
export const RIGHT_SIDEBAR_MAX_WIDTH = 900;

// Triage notification panel width in px (persisted). Narrower than the report
// sidebar: the panel carries a short flag list and one key image. Kept separate
// from `rightSidebarWidthAtom` so resizing one does not resize the other; the
// same min/max bounds apply.
const TRIAGE_PANEL_DEFAULT_WIDTH = 380;
export const triagePanelWidthAtom = atomWithStorage("triagePanelWidth", TRIAGE_PANEL_DEFAULT_WIDTH);

// Reporting drafter pane width in px (persisted). Default matches the previous
// fixed `w-lg` (Tailwind v4 `--container-lg` = 32rem = 512px). Kept separate
// from `rightSidebarWidthAtom` (dictation viewer) so the two contexts persist
// independently. Max is generous — radiologists asked for a lot more drafting
// room. Bounds enforced at the resize handler.
export const reportPaneWidthAtom = atomWithStorage("reportPaneWidth", 512);
export const REPORT_PANE_MIN_WIDTH = 360;
export const REPORT_PANE_MAX_WIDTH = 1000;

// Surfacer-mode vertical split (fraction of right-sidebar height taken by the
// top SurfacerPanel; rest goes to OriginalReportPanel). Persisted so a verifier
// cycling through a worklist keeps their preferred split across remounts.
export const surfacerSplitAtom = atomWithStorage("surfacerSplit", 0.55);
export const SURFACER_SPLIT_MIN = 0.15;
export const SURFACER_SPLIT_MAX = 0.85;

// Right panel active tab
export const rightPanelTabAtom = atom<"findings" | "report">("findings");

// Report config panel open state
export const reportConfigOpenAtom = atom(false);

// Focus mode - fills entire CaseViewer area with single viewport (hides sidebars)
export const focusModeAtom = atom<{
  enabled: boolean;
  slotId: SlotId | null;
}>({ enabled: false, slotId: null });

// =============================================================================
// Findings Display State
// =============================================================================

// Sort mode for findings list (persisted to localStorage)
export type FindingsSortMode = "confidence" | "importance" | "organ";
export const findingsSortModeAtom = atomWithStorage<FindingsSortMode>(
  "findingsSortMode",
  "confidence",
);

// View mode for findings list: by confidence tier or by spatial location (persisted)
export type FindingsViewMode = "confidence" | "location";
export const findingsViewModeAtom = atomWithStorage<FindingsViewMode>(
  "findingsViewMode",
  "confidence",
);

// =============================================================================
// Viewer State
// =============================================================================

// Is generating report
export const isGeneratingReportAtom = atom(false);

// Initial load complete flag (for first render optimizations)
export const initialLoadCompleteAtom = atom(false);

// Series loading state
export const isSeriesLoadingAtom = atom(false);

// =============================================================================
// Keyboard Hints State
// =============================================================================

// Has the user seen the keyboard shortcuts hint (persisted to localStorage)
export const keyboardHintsShownAtom = atomWithStorage("keyboardHintsShown", false);
